/**
 * The World: every entity of the open level, held as one immutable snapshot.
 *
 * Access is split by capability (review F-1):
 * - `reader` is handed to anyone: the Outliner, the render bridge, commands'
 *   validation.
 * - `writer` is only ever passed into command definitions (world/commands.ts)
 *   and the level commit. Nothing else holds it, so every mutation goes
 *   through the Command Bus (CLAUDE.md rule 1).
 *
 * Every write builds a new snapshot (copy-on-write map; only the changed
 * record is new), emits a synchronous event for systems like the render
 * bridge, and publishes to the store. React is notified once per batch.
 */
import type { ChangeScheduler } from "../core/ChangeScheduler";
import { Emitter } from "../core/Emitter";
import type { Unsubscribe } from "../core/Emitter";
import { VersionedStore } from "../core/store";
import type { ReadableStore } from "../core/store";
import type { EntityId, EntityRecord } from "./entity";
import { subtreeOf } from "./hierarchy";
import { findEntity } from "./names";
import type { ComponentData } from "./schema/defineComponent";

export interface WorldSnapshot {
  /** The open level's id, or null before any level is loaded. */
  readonly levelId: string | null;
  readonly entities: ReadonlyMap<EntityId, EntityRecord>;
  /** Top-level entities in order. */
  readonly roots: readonly EntityId[];
}

/** A detached world built off-world during level prepare (review F-4), then committed. */
export type WorldData = WorldSnapshot;

export interface WorldEvents {
  /** An entity was added, removed or changed. `previous`/`next` are null for add/remove. */
  "entity:changed": {
    readonly id: EntityId;
    readonly previous: EntityRecord | null;
    readonly next: EntityRecord | null;
  };
  "component:changed": {
    readonly id: EntityId;
    readonly type: string;
    readonly previous: ComponentData | undefined;
    readonly next: ComponentData | undefined;
  };
  "world:replaced": { readonly previous: WorldSnapshot; readonly next: WorldSnapshot };
}

export interface WorldReader extends ReadableStore<WorldSnapshot> {
  readonly events: Emitter<WorldEvents>;
  get(id: EntityId): EntityRecord | undefined;
  /** Looks up by id, then path, then a bare name only when it's unique (see names.ts). */
  resolve(idOrName: string): EntityRecord | undefined;
  component(id: EntityId, type: string): ComponentData | undefined;
}

export interface WorldWriter {
  setComponent(id: EntityId, type: string, data: ComponentData): void;
  removeComponent(id: EntityId, type: string): void;
  /** Turns a component off (kept, but acting as if absent) or back on. */
  setComponentEnabled(id: EntityId, type: string, enabled: boolean): void;
  rename(id: EntityId, name: string): void;
  setVisible(id: EntityId, visible: boolean): void;
  /** Removes an entity with all its descendants, detaching it from its parent (or the roots). */
  remove(id: EntityId): void;
  /**
   * Adds a subtree back: `records` are its entities with the subtree's root
   * first, which goes under `parent` (null for a root) at `index` among its siblings.
   */
  insert(records: readonly EntityRecord[], parent: EntityId | null, index: number): void;
  /**
   * Moves an entity (its subtree comes along) under `parent` (null: the
   * roots), at `index` among the new siblings not counting itself.
   */
  move(id: EntityId, parent: EntityId | null, index: number): void;
  /** Swaps in a whole prepared world (level commit). */
  replace(data: WorldData): void;
}

const EMPTY_WORLD: WorldSnapshot = { levelId: null, entities: new Map(), roots: [] };

export class World {
  readonly reader: WorldReader;
  readonly writer: WorldWriter;

  private readonly store: VersionedStore<WorldSnapshot>;
  private readonly events = new Emitter<WorldEvents>();

  constructor(scheduler: ChangeScheduler) {
    this.store = new VersionedStore(scheduler, EMPTY_WORLD);
    this.reader = this.createReader();
    this.writer = this.createWriter();
  }

  private createReader(): WorldReader {
    const snapshot = () => this.store.getSnapshot();

    return {
      events: this.events,
      subscribe: (listener): Unsubscribe => this.store.subscribe(listener),
      getSnapshot: snapshot,
      get: (id) => snapshot().entities.get(id),
      resolve: (reference) => {
        const { entities } = snapshot();
        const lookup = findEntity(entities, reference);
        return "found" in lookup ? entities.get(lookup.found) : undefined;
      },
      component: (id, type) => snapshot().entities.get(id)?.components.get(type),
    };
  }

  private createWriter(): WorldWriter {
    return {
      setComponent: (id, type, data) => {
        const previous = this.require(id);
        const components = new Map(previous.components);
        components.set(type, data);
        this.commitRecord({ ...previous, components });
        this.events.emit("component:changed", {
          id,
          type,
          previous: previous.components.get(type),
          next: data,
        });
      },
      removeComponent: (id, type) => {
        const previous = this.require(id);
        const components = new Map(previous.components);
        components.delete(type);
        this.commitRecord({ ...previous, components });
        this.events.emit("component:changed", {
          id,
          type,
          previous: previous.components.get(type),
          next: undefined,
        });
      },
      setComponentEnabled: (id, type, enabled) => {
        const previous = this.require(id);
        const disabled = new Set(previous.disabled);
        if (enabled) disabled.delete(type);
        else disabled.add(type);
        const { disabled: _old, ...rest } = previous;
        this.commitRecord(disabled.size > 0 ? { ...rest, disabled } : rest);
      },
      rename: (id, name) => {
        this.commitRecord({ ...this.require(id), name });
      },
      setVisible: (id, visible) => {
        this.commitRecord({ ...this.require(id), visible });
      },
      remove: (id) => {
        this.removeSubtree(id);
      },
      insert: (records, parent, index) => {
        this.insertSubtree(records, parent, index);
      },
      move: (id, parent, index) => {
        this.moveEntity(id, parent, index);
      },
      replace: (data) => {
        const previous = this.store.getSnapshot();
        this.store.publish(data);
        this.events.emit("world:replaced", { previous, next: data });
      },
    };
  }

  private removeSubtree(id: EntityId): void {
    const snapshot = this.store.getSnapshot();
    const record = this.require(id);
    const removed = subtreeOf(snapshot.entities, id).map((each) => this.require(each));

    const entities = new Map(snapshot.entities);
    for (const each of removed) entities.delete(each.id);
    const detached = this.withChildren(entities, record.parent, (ids) =>
      ids.filter((child) => child !== id),
    );
    const roots =
      record.parent === null ? snapshot.roots.filter((root) => root !== id) : snapshot.roots;
    this.store.publish({ ...snapshot, entities, roots });

    this.announceParent(detached);
    for (const each of removed) {
      for (const [type, data] of each.components) {
        this.events.emit("component:changed", {
          id: each.id,
          type,
          previous: data,
          next: undefined,
        });
      }
      this.events.emit("entity:changed", { id: each.id, previous: each, next: null });
    }
  }

  private insertSubtree(
    records: readonly EntityRecord[],
    parent: EntityId | null,
    index: number,
  ): void {
    const [top] = records;
    if (!top) return;
    const snapshot = this.store.getSnapshot();

    const entities = new Map(snapshot.entities);
    for (const each of records) entities.set(each.id, each);
    const place = (ids: readonly EntityId[]) => insertAt(ids, top.id, index);
    const attached = this.withChildren(entities, parent, place);
    const roots = parent === null ? place(snapshot.roots) : snapshot.roots;
    this.store.publish({ ...snapshot, entities, roots });

    this.announceParent(attached);
    for (const each of records) {
      this.events.emit("entity:changed", { id: each.id, previous: null, next: each });
      for (const [type, data] of each.components) {
        this.events.emit("component:changed", {
          id: each.id,
          type,
          previous: undefined,
          next: data,
        });
      }
    }
  }

  private moveEntity(id: EntityId, parent: EntityId | null, index: number): void {
    const snapshot = this.store.getSnapshot();
    const record = this.require(id);
    const moved = { ...record, parent };

    const entities = new Map(snapshot.entities);
    entities.set(id, moved);
    const without = (ids: readonly EntityId[]) => ids.filter((each) => each !== id);
    this.withChildren(entities, record.parent, without);
    this.withChildren(entities, parent, (ids) => insertAt(without(ids), id, index));
    let roots = record.parent === null ? without(snapshot.roots) : snapshot.roots;
    if (parent === null) roots = insertAt(without(roots), id, index);
    this.store.publish({ ...snapshot, entities, roots });

    // Each affected parent once (old and new may be the same), then the entity.
    for (const changed of new Set([record.parent, parent])) {
      if (changed === null) continue;
      const previous = snapshot.entities.get(changed) ?? null;
      this.events.emit("entity:changed", {
        id: changed,
        previous,
        next: entities.get(changed) ?? null,
      });
    }
    this.events.emit("entity:changed", { id, previous: record, next: moved });
  }

  /**
   * Rewrites a parent's child list in `entities` (no parent: nothing), reading
   * the parent from `entities` so successive rewrites compose. Returns the change.
   */
  private withChildren(
    entities: Map<EntityId, EntityRecord>,
    parent: EntityId | null,
    update: (children: readonly EntityId[]) => EntityId[],
  ): { previous: EntityRecord; next: EntityRecord } | null {
    if (parent === null) return null;
    const previous = entities.get(parent);
    if (!previous) throw new Error(`Unknown entity '${parent}'`);
    const next = { ...previous, children: update(previous.children) };
    entities.set(parent, next);
    return { previous, next };
  }

  private announceParent(change: { previous: EntityRecord; next: EntityRecord } | null): void {
    if (change) this.events.emit("entity:changed", { id: change.next.id, ...change });
  }

  /** Publishes a snapshot with one record replaced, then announces the change. */
  private commitRecord(next: EntityRecord): void {
    const snapshot = this.store.getSnapshot();
    const previous = snapshot.entities.get(next.id) ?? null;

    const entities = new Map(snapshot.entities);
    entities.set(next.id, next);
    this.store.publish({ ...snapshot, entities });

    this.events.emit("entity:changed", { id: next.id, previous, next });
  }

  private require(id: EntityId): EntityRecord {
    const record = this.store.getSnapshot().entities.get(id);
    if (!record) throw new Error(`Unknown entity '${id}'`);
    return record;
  }
}

/** `ids` with `id` inserted at `index` (clamped to the end). */
function insertAt(ids: readonly EntityId[], id: EntityId, index: number): EntityId[] {
  return [...ids.slice(0, index), id, ...ids.slice(index)];
}
